import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/crmApi";
import { PageHeader, Loader, Empty } from "@/crm/ui";
import { toast } from "sonner";
import { Zap, Plus, Play, Trash2, Pencil, X, MapPin } from "lucide-react";
import Modal from "@/crm/components/Modal";

export default function Workflows() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/workflows").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async (id) => {
    try {
      const { data } = await api.post(`/workflows/${id}/run`);
      toast.success(`Exécuté : ${data.executed} action(s), ${data.skipped} déjà traitée(s)`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/workflows/${id}`); toast.success("Supprimé"); load(); };
  const toggle = async (wf) => {
    await api.put(`/workflows/${wf.id}`, { name: wf.name, trigger: wf.trigger, conditions: wf.conditions || {}, actions: wf.actions || [], active: !(wf.active !== false) });
    load();
  };

  if (!items) return <Loader />;

  return (
    <div>
      <PageHeader title="Automatisations" subtitle="Règles automatiques (workflows) et attribution géographique"
        actions={<button data-testid="new-workflow-btn" onClick={() => setEditing({})} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700"><Plus size={16} /> Nouvelle règle</button>} />

      {items.length === 0 ? <Empty label="Aucune règle — automatisez les relances de prospects inactifs" icon={Zap} /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {items.map((wf) => (
            <div key={wf.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`workflow-${wf.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2">
                    <Zap size={16} className="text-amber-500" /> {wf.name}
                  </h3>
                  <div className="text-xs text-slate-400 mt-1">
                    Prospect inactif depuis {(wf.conditions?.days) || (wf.trigger?.match(/\d+/)?.[0]) || 5} jours
                    {wf.conditions?.sector ? ` · secteur ${wf.conditions.sector}` : ""}
                  </div>
                </div>
                <button onClick={() => toggle(wf)} className={`text-xs px-2 py-0.5 rounded-md ${wf.active !== false ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {wf.active !== false ? "Active" : "Inactive"}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(wf.actions || []).map((a, i) => (
                  <span key={i} className="text-xs bg-indigo-50 text-indigo-700 rounded-md px-2 py-0.5">
                    {a.type === "create_task" ? `📋 Tâche : ${a.label || "Relancer"}` : "✉️ Email auto"}
                  </span>
                ))}
              </div>
              {wf.last_run_at && (
                <div className="text-[11px] text-slate-400 mt-2">
                  Dernière exécution : {new Date(wf.last_run_at).toLocaleString("fr-FR")}
                  {wf.last_run_stats ? ` · ${wf.last_run_stats.executed} traitée(s)` : ""}
                </div>
              )}
              <div className="flex gap-2 mt-4">
                <button data-testid={`run-workflow-${wf.id}`} onClick={() => run(wf.id)} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800"><Play size={14} /> Exécuter maintenant</button>
                <button onClick={() => setEditing(wf)} className="px-3 py-2 rounded-lg border border-slate-300 text-slate-500 hover:text-indigo-600"><Pencil size={15} /></button>
                <button onClick={() => del(wf.id)} className="px-3 py-2 rounded-lg border border-slate-300 text-slate-400 hover:text-rose-500"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ZoneAssignments />

      {editing !== null && <WorkflowModal workflow={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function WorkflowModal({ workflow, onClose, onSaved }) {
  const isNew = !workflow.id;
  const [name, setName] = useState(workflow.name || "");
  const [days, setDays] = useState(workflow.conditions?.days || 5);
  const [sector, setSector] = useState(workflow.conditions?.sector || "");
  const [sectors, setSectors] = useState([]);
  const [templates, setTemplates] = useState([]);
  const initial = workflow.actions || [];
  const [taskOn, setTaskOn] = useState(isNew ? true : initial.some((a) => a.type === "create_task"));
  const [taskLabel, setTaskLabel] = useState(initial.find((a) => a.type === "create_task")?.label || "Relancer");
  const [emailOn, setEmailOn] = useState(initial.some((a) => a.type === "send_email"));
  const [templateId, setTemplateId] = useState(initial.find((a) => a.type === "send_email")?.template_id || "");

  useEffect(() => {
    api.get("/sectors").then(({ data }) => setSectors(data)).catch(() => {});
    api.get("/email-templates").then(({ data }) => setTemplates(data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!name) return toast.error("Nom requis");
    if (!taskOn && !emailOn) return toast.error("Sélectionnez au moins une action");
    if (emailOn && !templateId) return toast.error("Choisissez un template d'email");
    const actions = [];
    if (taskOn) actions.push({ type: "create_task", label: taskLabel });
    if (emailOn) actions.push({ type: "send_email", template_id: templateId });
    const payload = {
      name, trigger: `prospect_inactive_${days}days`,
      conditions: { days: Number(days), ...(sector ? { sector } : {}) },
      actions, active: workflow.active !== false,
    };
    try {
      if (isNew) await api.post("/workflows", payload);
      else await api.put(`/workflows/${workflow.id}`, payload);
      toast.success(isNew ? "Règle créée" : "Règle mise à jour");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Modal onClose={onClose} size="lg" testid="workflow-modal"
      label={isNew ? "Nouvelle règle" : "Modifier la règle"}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-lg">{isNew ? "Nouvelle règle" : "Modifier la règle"}</h3>
        <button onClick={onClose}><X size={18} /></button>
      </div>
      <div className="space-y-4">
        <input data-testid="workflow-name" placeholder="Nom (ex: Relance restaurants inactifs)" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500">Déclencheur : inactif depuis (jours)</label>
            <input data-testid="workflow-days" type="number" min={0} value={days} onChange={(e) => setDays(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Condition : secteur (optionnel)</label>
            <select data-testid="workflow-sector" value={sector} onChange={(e) => setSector(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
              <option value="">Tous les secteurs</option>
              {sectors.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
            </select>
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-xs text-slate-500 uppercase font-medium">Actions</label>
          <label className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 cursor-pointer">
            <input type="checkbox" data-testid="workflow-action-task" checked={taskOn} onChange={(e) => setTaskOn(e.target.checked)} />
            <span className="text-sm">Créer une tâche</span>
            {taskOn && <input value={taskLabel} onChange={(e) => setTaskLabel(e.target.value)} className="flex-1 px-2 py-1 rounded border border-slate-300 text-sm" placeholder="Libellé" />}
          </label>
          <label className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 cursor-pointer">
            <input type="checkbox" data-testid="workflow-action-email" checked={emailOn} onChange={(e) => setEmailOn(e.target.checked)} />
            <span className="text-sm">Envoyer un email</span>
            {emailOn && (
              <select data-testid="workflow-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="flex-1 px-2 py-1 rounded border border-slate-300 text-sm bg-white">
                <option value="">— Template —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
          </label>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm">Annuler</button>
        <button data-testid="save-workflow" onClick={save} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700">{isNew ? "Créer" : "Enregistrer"}</button>
      </div>
    </Modal>
  );
}

function ZoneAssignments() {
  const [zones, setZones] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [prefix, setPrefix] = useState("");
  const [commercialId, setCommercialId] = useState("");

  const load = () => api.get("/zone-assignments").then(({ data }) => setZones(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/users/commercials").then(({ data }) => setCommercials(data)).catch(() => {});
  }, []);

  const add = async () => {
    if (!prefix || !commercialId) return toast.error("Préfixe et commercial requis");
    try {
      await api.post("/zone-assignments", { code_postal_prefix: prefix, commercial_id: commercialId });
      toast.success("Zone attribuée"); setPrefix(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/zone-assignments/${id}`); load(); };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="zone-assignments">
      <h3 className="font-heading font-semibold text-slate-800 mb-1 flex items-center gap-2"><MapPin size={17} /> Attribution géographique automatique</h3>
      <p className="text-xs text-slate-400 mb-4">À l'import CSV ou à l'extraction, les nouveaux prospects dont le code postal correspond sont attribués automatiquement (l'assignation manuelle reste prioritaire).</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <input data-testid="zone-prefix" placeholder="Préfixe CP (ex: 69)" value={prefix} onChange={(e) => setPrefix(e.target.value)} className="w-40 px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        <select data-testid="zone-commercial" value={commercialId} onChange={(e) => setCommercialId(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
          <option value="">— Commercial —</option>
          {commercials.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button data-testid="add-zone" onClick={add} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700">Ajouter</button>
      </div>
      {zones.length === 0 ? <p className="text-sm text-slate-400">Aucune zone configurée</p> : (
        <div className="flex flex-wrap gap-2">
          {zones.map((z) => (
            <span key={z.id} className="inline-flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-1.5 text-sm" data-testid={`zone-${z.id}`}>
              <span className="font-mono font-semibold">{z.code_postal_prefix}</span>
              <span className="text-slate-500">→ {z.commercial_name}</span>
              <button onClick={() => del(z.id)} className="text-slate-400 hover:text-rose-500"><X size={13} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
